import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { confirm, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import type { ReviewDTO } from "../api/client";
import {
  useDeleteReview,
  useReviewsQuery,
  useUpdateReviewHidden,
} from "../api/queries";
import { ReviewDetailDialog } from "../ui/ReviewDetailDialog";
import { ReviewsFilter } from "../ui/ReviewsFilter";
import { ReviewsTable } from "../ui/ReviewsTable";
import { ReviewsToolbar } from "../ui/ReviewsToolbar";

export function ReviewsPage() {
  const nav = useNavigate({ from: "/app/reviews" });
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/reviews" });
  const list = useReviewsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["reviews:update", "reviews:delete"]);
  const updateHidden = useUpdateReviewHidden();
  const deleteReview = useDeleteReview();

  const handleToggleHidden = (row: ReviewDTO) => {
    const newHidden = !row.hidden;
    toast.promise(updateHidden.mutateAsync({ id: row.id, hidden: newHidden }), {
      loading: "ກຳລັງປ່ຽນສະຖານະ...",
      success: newHidden ? "ຊ້ອນສຳເລັດ" : "ເປີດໃຫ້ສະແດງສຳເລັດ",
      error: "ປ່ຽນສະຖານະລົ້ມເຫຼວ",
    });
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: "ລຶບຄຳເຫັນ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຄຳເຫັນນີ້?",
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (ok) {
      toast.promise(deleteReview.mutateAsync(id), {
        loading: "ກຳລັງລຶບ...",
        success: "ລຶບສຳເລັດ",
        error: "ລຶບລົ້ມເຫຼວ",
      });
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຄຳເຫັນສິນຄ້າ" },
          ]}
        />
      </Header>
      <Main>
        <ReviewsToolbar />
        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ReviewsFilter />
          <ReviewsTable
            canManage={!!canManage}
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            sortBy={search.sort?.[0]?.field}
            sortOrder={search.sort?.[0]?.dir as "asc" | "desc" | undefined}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onView={(row) => {
              setSelectedReviewId(row.id);
              setDetailOpen(true);
            }}
            onToggleHidden={handleToggleHidden}
            onDelete={handleDelete}
          />
        </div>

        <ReviewDetailDialog
          open={detailOpen}
          onOpenChange={(open) => {
            setDetailOpen(open);
            if (!open) setSelectedReviewId(null);
          }}
          reviewId={selectedReviewId}
        />
      </Main>
    </>
  );
}
